import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import FormActions from "./event-form/FormActions";
import EventFormFields from "./event-form/EventFormFields";
import { useCreateEvent } from "@/hooks/use-create-event";
import { getEventFormInit } from "@/hooks/use-event-form-init";
import { EventFormData } from "./event-form/types";
import { Event } from "@/types/event";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateEventFormProps {
  event?: Event;
  onClose: () => void;
}

const CreateEventForm = ({ event, onClose }: CreateEventFormProps) => {
  const [tags, setTags] = useState<string[]>(event?.tags || []);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(
    event?.latitude && event?.longitude 
      ? { lat: event.latitude, lng: event.longitude }
      : null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [hostType, setHostType] = useState<string>(event?.host || "member");

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<EventFormData>();

  useEffect(() => {
    const initForm = async () => {
      const defaultValues = await getEventFormInit(event);
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key as keyof EventFormData, value);
      });
      setIsLoading(false);
    };
    
    initForm();
  }, [event, setValue]);

  const { createEvent, isSubmitting } = useCreateEvent(onClose);

  const handleLocationSelect = (venue: string, lat: number, lng: number) => {
    setValue("venue", venue);
    setCoordinates({ lat, lng });
  };

  const onSubmit = async (data: EventFormData) => {
    if (groupsError) {
      toast.error(groupsError);
      return;
    }
    const eventId = await createEvent(data, coordinates, tags, hostType, event?.id);
    if (!eventId) return; // Add this check to fix the build error

    // Handle event locations
    if (event?.id) {
      // Delete existing locations
      await supabase
        .from('event_locations')
        .delete()
        .eq('event_id', event.id);
    }
    
    // Insert new locations
    if (data.locations?.length > 0) {
      const eventLocations = data.locations.map(loc => ({
        event_id: eventId,
        ...loc
      }));
      
      await supabase
        .from('event_locations')
        .insert(eventLocations);
    }
  };

  if (isLoading) {
    return <div className="bg-white rounded-lg p-6 shadow-lg">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">
          {event ? 'Edit Event' : 'Create New Event'}
        </h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <EventFormFields
          register={register}
          watch={watch}
          errors={errors}
          setValue={setValue}
          tags={tags}
          hostType={hostType}
          setHostType={setHostType}
          onTagsChange={setTags}
          onLocationSelect={handleLocationSelect}
          defaultVenue={event?.venue}
          defaultHost={event?.host}
          eventId={event?.id}
          onGroupsValidityChange={setGroupsError}
        />

        <FormActions 
          onClose={onClose} 
          isSubmitting={isSubmitting} 
          isEditing={!!event}
        />
      </form>
    </div>
  );
};

export default CreateEventForm;